<template>
  <div class="rich-text-editor">
    <div class="toolbar">
      <v-btn
        small
        text
        @click="execCommand('bold')"
        :class="{ active: isBold }"
      >
        <v-icon small>mdi-format-bold</v-icon>
      </v-btn>
      <v-btn
        small
        text
        @click="execCommand('italic')"
        :class="{ active: isItalic }"
      >
        <v-icon small>mdi-format-italic</v-icon>
      </v-btn>
      <v-btn
        small
        text
        @click="execCommand('underline')"
        :class="{ active: isUnderline }"
      >
        <v-icon small>mdi-format-underline</v-icon>
      </v-btn>
      <v-btn
        small
        text
        @click="execCommand('backColor', '#ffff00')"
        :class="{ active: isHighlighted }"
      >
        <v-icon small>mdi-format-color-highlight</v-icon>
      </v-btn>
      <v-btn small text @click="openLinkDialog" :class="{ active: isLink }">
        <v-icon small>mdi-link</v-icon>
      </v-btn>
      <v-btn
        small
        text
        @mousedown.prevent="wrapSelectionInBlockquote"
        :class="{ active: isBlockquote }"
        title="Цитата (выделите текст и нажмите)"
      >
        <v-icon small>mdi-format-quote-close</v-icon>
      </v-btn>
    </div>
    <v-dialog v-model="showLinkDialog" max-width="500">
      <v-card>
        <v-card-title>Добавить ссылку</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="linkUrl"
            label="URL"
            placeholder="https://example.com"
            outlined
            @keyup.enter="insertLink"
          />
          <div v-if="selectedText" class="mt-2 text-caption">
            Выделенный текст: "{{ selectedText }}"
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelLinkDialog">Отмена</v-btn>
          <v-btn color="primary" @click="insertLink" :disabled="!linkUrl"
            >Добавить</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <div
      ref="editor"
      contenteditable="true"
      class="editor-content"
      @input="handleInput"
      @blur="handleBlur"
      @keydown.enter.exact.prevent="insertLineBreak()"
    ></div>
  </div>
</template>

<script>
export default {
  name: "NewsRichTextEditor",
  props: {
    value: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      localValue: this.value || "",
      showLinkDialog: false,
      linkUrl: "",
      selectedText: "",
      savedRange: null,
    };
  },
  watch: {
    value(newVal) {
      if (newVal !== this.localValue) {
        this.localValue = newVal || "";
        this.updateEditorContent();
      }
    },
  },
  mounted() {
    this.updateEditorContent();
  },
  computed: {
    isBold() {
      if (!this.$refs.editor) return false;
      return document.queryCommandState("bold");
    },
    isItalic() {
      if (!this.$refs.editor) return false;
      return document.queryCommandState("italic");
    },
    isUnderline() {
      if (!this.$refs.editor) return false;
      return document.queryCommandState("underline");
    },
    isHighlighted() {
      if (!this.$refs.editor) return false;
      const selection = window.getSelection();
      if (selection.rangeCount === 0) return false;
      const range = selection.getRangeAt(0);
      const container = range.commonAncestorContainer;
      const element =
        container.nodeType === 3 ? container.parentElement : container;
      if (element) {
        const bgColor = window.getComputedStyle(element).backgroundColor;
        return bgColor === "rgb(255, 255, 0)" || bgColor === "yellow";
      }
      return false;
    },
    isLink() {
      if (!this.$refs.editor) return false;
      const selection = window.getSelection();
      if (selection.rangeCount === 0) return false;
      const range = selection.getRangeAt(0);
      let element = range.commonAncestorContainer;
      if (element.nodeType === 3) {
        element = element.parentElement;
      }
      while (element && element !== this.$refs.editor) {
        if (element.tagName === "A") {
          return true;
        }
        element = element.parentElement;
      }
      return false;
    },
    isBlockquote() {
      if (!this.$refs.editor) return false;
      const selection = window.getSelection();
      if (selection.rangeCount === 0) return false;
      let element = selection.getRangeAt(0).commonAncestorContainer;
      if (element.nodeType === 3) element = element.parentElement;
      while (element && element !== this.$refs.editor) {
        if (element.tagName === "BLOCKQUOTE") return true;
        element = element.parentElement;
      }
      return false;
    },
  },
  methods: {
    updateEditorContent() {
      if (this.$refs.editor) {
        const content = this.localValue || "";
        this.$refs.editor.innerHTML = content;
      }
    },
    handleInput() {
      if (this.$refs.editor) {
        this.localValue = this.$refs.editor.innerHTML;
        this.$emit("input", this.localValue);
      }
    },
    handleBlur() {
      if (this.$refs.editor) {
        this.localValue = this.$refs.editor.innerHTML;
        this.$emit("input", this.localValue);
      }
    },
    execCommand(command, value = null) {
      this.$refs.editor.focus();
      document.execCommand(command, false, value);
      this.handleInput();
    },
    wrapSelectionInBlockquote() {
      const editor = this.$refs.editor;
      if (!editor) return;

      const selection = window.getSelection();
      if (selection.rangeCount === 0) return;
      const range = selection.getRangeAt(0);
      if (!editor.contains(range.startContainer)) return;

      const selectedText = selection.toString();
      if (!selectedText.trim()) {
        return;
      }

      const blockquote = document.createElement("blockquote");
      try {
        const fragment = range.extractContents();
        blockquote.appendChild(fragment);
        range.insertNode(blockquote);
        const newRange = document.createRange();
        newRange.setStartAfter(blockquote);
        newRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(newRange);
      } catch (e) {
        document.execCommand(
          "insertHTML",
          false,
          "<blockquote>" + selection.toString() + "</blockquote>"
        );
      }
      this.handleInput();
    },
    insertLineBreak() {
      document.execCommand("insertHTML", false, "<br>");
      this.handleInput();
    },
    insertParagraph() {
      document.execCommand("insertHTML", false, "<br>");
      this.handleInput();
    },
    openLinkDialog() {
      const selection = window.getSelection();
      if (selection.rangeCount === 0) {
        this.selectedText = "";
        alert("Выделите текст, который нужно обернуть в ссылку");
        return;
      }

      const range = selection.getRangeAt(0);
      this.selectedText = selection.toString();

      if (!this.selectedText || this.selectedText.trim() === "") {
        alert("Выделите текст, который нужно обернуть в ссылку");
        return;
      }

      this.savedRange = range.cloneRange();
      this.showLinkDialog = true;
      this.linkUrl = "";
    },
    cancelLinkDialog() {
      this.showLinkDialog = false;
      this.linkUrl = "";
      this.selectedText = "";
      this.savedRange = null;
    },
    insertLink() {
      if (!this.linkUrl) return;
      if (!this.selectedText || !this.savedRange) return;

      this.$refs.editor.focus();

      try {
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(this.savedRange);

        const linkElement = document.createElement("a");
        linkElement.href = this.linkUrl;
        linkElement.target = "_blank";
        linkElement.rel = "noopener noreferrer";
        linkElement.textContent = this.selectedText;

        this.savedRange.deleteContents();
        this.savedRange.insertNode(linkElement);

        const newRange = document.createRange();
        newRange.setStartAfter(linkElement);
        newRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(newRange);

        this.$nextTick(() => {
          this.handleInput();
          this.cancelLinkDialog();
        });
      } catch (error) {
        console.error("Error inserting link:", error);
        this.cancelLinkDialog();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.rich-text-editor {
  border: 1px solid #23919e;
  border-radius: 4px;
  background-color: white;
}

.toolbar {
  display: flex;
  gap: 4px;
  padding: 4px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #23919e;

  .v-btn {
    min-width: 36px;
  }

  .v-btn.active {
    background-color: #23919e;
    color: white;
  }
}

.editor-content {
  min-height: 120px;
  padding: 12px;
  outline: none;
  overflow-y: auto;
  line-height: 1.5;
  font-size: 14px;
  font-family: inherit;
  word-wrap: break-word;

  :deep(a) {
    color: #1976d2;
    text-decoration: underline;
    cursor: pointer;

    &:hover {
      color: #1565c0;
    }
  }

  :deep(blockquote) {
    margin: 8px 0;
    padding: 8px 16px;
    border-left: 4px solid #23919e;
    background-color: #f5f5f5;
    color: #333;
    font-style: italic;
  }

  &:empty:before {
    content: attr(placeholder);
    color: #999;
    pointer-events: none;
  }
}
</style>
